import { memo, useMemo } from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip as ChartTooltip,
} from 'recharts';
import { Empty, Skeleton } from 'antd';
import { Panel } from './Panel';
import { useWidgetData, groupByCategory, defaultPalette, currencyFmt } from './shared';
import type { PieChartDashboardWidget, DonutChartDashboardWidget } from '../../types/panelConfig';

interface Props {
  widget: PieChartDashboardWidget | DonutChartDashboardWidget;
  sourceId: number | null | undefined;
  variant?: 'pie' | 'donut';
  parameters?: Record<string, string>;
}

function PieChartWidgetImpl({ widget, sourceId, variant, parameters }: Props) {
  const { data, isLoading } = useWidgetData(sourceId, `${widget.type}-${widget.reportCode}`, parameters);
  const {
    categoryField,
    valueField,
    aggregation = 'sum',
    topN,
    title,
    subtitle,
    color,
    showLabels,
  } = widget.config;

  const palette = Array.isArray(color) && color.length > 0 ? color : defaultPalette;
  const innerRadius = (variant ?? (widget.type === 'chart-donut' ? 'donut' : 'pie')) === 'donut' ? 54 : 0;
  const testId = widget.type === 'chart-donut' ? 'chart-donut-widget' : 'chart-pie-widget';

  const series = useMemo(() => {
    const rows = data ?? [];
    const grouped = groupByCategory(rows, categoryField, valueField, aggregation);
    const sorted = grouped.sort((a, b) => b.value - a.value);
    return topN ? sorted.slice(0, topN) : sorted;
  }, [data, categoryField, valueField, aggregation, topN]);

  return (
    <div data-testid={testId}>
      <Panel title={title} subtitle={subtitle}>
        {isLoading ? (
          <Skeleton.Node active style={{ width: '100%', height: 260 }} />
        ) : series.length === 0 ? (
          <Empty description="Veri yok" style={{ padding: 40 }} />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={series}
                dataKey="value"
                nameKey="name"
                innerRadius={innerRadius}
                outerRadius={82}
                paddingAngle={1}
                label={
                  showLabels
                    ? (e: { percent?: number }) => `%${Math.round((e.percent ?? 0) * 100)}`
                    : undefined
                }
                labelLine={false}
              >
                {series.map((_, i) => (
                  <Cell key={i} fill={palette[i % palette.length]} />
                ))}
              </Pie>
              <ChartTooltip
                formatter={(v) => currencyFmt.format(Number(v))}
                contentStyle={{ borderRadius: 4, border: '1px solid var(--color-border)', fontSize: 12 }}
              />
              <Legend
                verticalAlign="bottom"
                align="center"
                iconSize={8}
                wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </Panel>
    </div>
  );
}

export const PieChartWidget = memo(PieChartWidgetImpl);
